import "./SnapWidgetPrivacyPolicy.css";

const updatedAt = "May 17, 2026";

const sections = [
  {
    title: "1. What Information We Collect",
    body: [
      "We collect information you provide directly, including names, email addresses, usernames, profile details, account credentials, and support messages.",
      "If you make a purchase, payment details are processed by Google. SnapWidget does not store full debit or credit card numbers.",
      "If you use app features that require permission, we may request access to device features such as camera, contacts, calendar, storage, and push notifications."
    ]
  },
  {
    title: "2. Information Collected Automatically",
    body: [
      "We may collect device and usage information such as device model, operating system, app version, IP address, event timestamps, crash data, and feature usage.",
      "This information helps us operate the app, troubleshoot issues, protect accounts, understand usage trends, and improve SnapWidget."
    ]
  },
  {
    title: "3. Google API Data",
    body: [
      "Our use of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements."
    ]
  },
  {
    title: "4. How We Process Information",
    body: [
      "We process information to create and manage accounts, deliver app features, respond to support requests, send administrative messages, protect our services, prevent fraud, analyze usage, and comply with legal obligations.",
      "Where required, we process information with your consent. You may withdraw consent at any time, subject to applicable law."
    ]
  },
  {
    title: "5. Legal Bases",
    body: [
      "Depending on where you live, we may rely on consent, performance of a contract, legitimate interests, legal obligations, or vital interests as the legal basis for processing your personal information.",
      "For users in the EEA, UK, Switzerland, and Canada, additional regional rights may apply."
    ]
  },
  {
    title: "6. When We Share Information",
    body: [
      "We may share information with vendors, consultants, service providers, payment processors, analytics providers, advertising networks, and other parties who help us operate SnapWidget.",
      "We may also share information during business transfers, when required by law, to protect rights and safety, or when you interact with public or shared areas of the service."
    ]
  },
  {
    title: "7. Cookies and Tracking",
    body: [
      "We may use cookies, pixels, web beacons, analytics tools, and similar technologies on our websites and services.",
      "These technologies help maintain security, prevent crashes, remember preferences, measure usage, and support advertising where permitted by law."
    ]
  },
  {
    title: "8. Social Logins",
    body: [
      "If you register or sign in using a social media account, we may receive profile information from that provider, such as your name, email address, profile picture, and other information you choose to make public.",
      "Your use of third-party social platforms is governed by those platforms' own privacy policies."
    ]
  },
  {
    title: "9. How Long We Keep Information",
    body: [
      "We keep personal information for as long as needed for the purposes described in this policy, unless a longer retention period is required or permitted by law.",
      "When we no longer have a legitimate need to process personal information, we delete or anonymize it. If deletion is not immediately possible, we securely store it and isolate it from further processing."
    ]
  },
  {
    title: "10. How We Keep Information Safe",
    body: [
      "We use reasonable technical and organizational safeguards designed to protect personal information.",
      "No electronic transmission or storage system can be guaranteed to be fully secure, so you should access SnapWidget only in a secure environment."
    ]
  },
  {
    title: "11. Children",
    body: [
      "We do not knowingly collect, solicit, sell, or market personal information from children under 18 years of age, or the equivalent age specified by law in your jurisdiction.",
      "If you believe we collected information from a child, contact us and we will take reasonable steps to delete it."
    ]
  },
  {
    title: "12. Your Privacy Rights",
    body: [
      "Depending on where you live, you may have rights to access, correct, delete, restrict, object to, or receive a copy of your personal information.",
      "You may also have rights to opt out of targeted advertising, sale or sharing of personal information, and certain profiling activities.",
      "We will review and respond to requests in accordance with applicable privacy laws."
    ]
  },
  {
    title: "13. Do-Not-Track",
    body: [
      "Some browsers and mobile systems include Do-Not-Track settings. Because no uniform technical standard has been finalized, we do not currently respond to Do-Not-Track browser signals."
    ]
  },
  {
    title: "14. United States State Privacy Rights",
    body: [
      "Residents of certain U.S. states, including California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, and Virginia, may have additional privacy rights.",
      "These rights may include knowing whether we process personal information, accessing it, correcting inaccuracies, requesting deletion, obtaining a portable copy, and opting out of targeted advertising, sale, sharing, or profiling where applicable."
    ]
  },
  {
    title: "15. Refunds",
    body: [
      "Refund requests may be deemed invalid if made more than 24 hours after purchase. Purchases through Google Play may also be subject to Google's billing and refund policies."
    ]
  },
  {
    title: "16. Updates",
    body: [
      "We may update this Privacy Policy from time to time. The updated version will be identified by the updated date above.",
      "If we make material changes, we may notify you through the app, website, or another appropriate method."
    ]
  },
  {
    title: "17. Contact Us",
    body: [
      "If you have questions, comments, or privacy requests, contact us at support@blackdoorapps.com."
    ]
  }
];

function SnapWidgetPrivacyPolicy() {
  return (
    <main className="snap-policy-page">
      <section className="snap-policy-hero">
        <a className="snap-policy-brand" href="/" aria-label="BlackDoor home">
          BlackDoor
        </a>
        <p className="snap-policy-kicker">SnapWidget</p>
        <h1>Privacy Policy</h1>
        <p className="snap-policy-updated">Last updated {updatedAt}</p>
        <p className="snap-policy-summary">
          This Privacy Policy explains how BlackDoor collects, uses, shares, and protects information when you use SnapWidget and related services.
        </p>
      </section>

      <section className="snap-policy-content" aria-label="Privacy policy content">
        <div className="snap-policy-card snap-policy-intro">
          <h2>Summary</h2>
          <p>
            We process information to operate SnapWidget, support accounts, improve product quality, protect users, process purchases, and comply with law. If you do not agree with this policy, please do not use SnapWidget.
          </p>
        </div>

        {sections.map((section) => (
          <article className="snap-policy-card" key={section.title}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </article>
        ))}

        <div className="snap-policy-card snap-policy-actions">
          <h2>Review, Update, or Delete Your Data</h2>
          <p>
            To request access, correction, deletion, or another privacy action, email{" "}
            <a href="mailto:support@blackdoorapps.com">support@blackdoorapps.com</a>.
          </p>
          <p>
            For payment privacy information, review Google's payment privacy notice at{" "}
            <a href="https://payments.google.com/payments/apis-secure/u/0/get_legal_document?ldo=0&ldt=privacynotice&ldl=en-GB" target="_blank" rel="noreferrer">
              Google Payments Privacy Notice
            </a>.
          </p>
        </div>
      </section>
    </main>
  );
}

export default SnapWidgetPrivacyPolicy;
